// Microbe v2 — the GENOME popup: a slide-up bottom sheet over the live petri-dish world.
//
// This is the "spend your currencies" surface, ported from v1 colony.ts's spec-tree card render
// but reshaped as a modal sheet (the world keeps living behind it, soft-paused + blurred). It owns
// NO game math: every read/write goes through GameCtx (state.ts is the single source of truth), so
// the sheet is a pure view. open() soft-pauses the world + reveals the sheet; close() resumes +
// hides. render() is idempotent — it only refreshes numbers + affordability, never rebuilds DOM —
// and is wired to ctx.onChange so buying a trait (or idle/feeding income) repaints the sheet live.
//
// CSS lives in style.css (written by the shell) under the /* genome */ block; this file injects no
// styles, only the documented class hooks: .genome / .gwallet / .branch / .gcard(.locked/.maxed/
// .rare/.deadend) / #genomeClose, plus body.genome-open for the world's blur/dim.

import { TRAITS } from './state.js';
import type { GameCtx, TraitDef, Branch, Wallet } from './types.js';

/** Wallet-currency icons (mirrors the HUD). */
const ICON: Record<keyof Wallet, string> = {
  energy: '⚡',
  biomass: '🧫',
  xp: '✨',
  enzymes: '🧪',
};

/** Order cost chips render in — scarce/gating currencies first so the enzyme gate reads clearly. */
const COST_ORDER: (keyof Wallet)[] = ['enzymes', 'xp', 'energy', 'biomass'];

/** Branch section order + labels (grouped headings inside the scrolling body). */
const BRANCH_ORDER: Branch[] = ['metabolism', 'defense', 'motility'];
const BRANCH_LABEL: Record<Branch, string> = {
  metabolism: 'Metabolism',
  defense: 'Defense',
  motility: 'Motility',
};

/** Compact readout: 942 · 1.2k · 3.4M. Keeps the wallet + cost chips narrow on a phone. */
function fmt(n: number): string {
  const v = Math.floor(n);
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** Per-card refresh closure — repaints level, lock/max state, and cost chips from live state. */
interface Card {
  update(): void;
}

export function mountGenome(
  host: HTMLElement,
  ctx: GameCtx,
): { open(): void; close(): void; render(): void } {
  host.innerHTML = '';

  // The HOST element (#genome) IS the bottom sheet: style.css styles #genome directly and drives
  // the slide-up purely off its [hidden] attribute (translateY 110% -> 0). So we build content
  // straight into the host and toggle host.hidden — there is no inner wrapper. (An inner wrapper
  // was the freeze bug: it left #genome[hidden] on forever, so the sheet stayed off-screen +
  // pointer-events:none while startPause() had frozen the world, with no reachable close button.)

  // ---- header: compact wallet readout + close button (sticky via CSS) ----
  const header = el('div', 'genome-head');

  const gwallet = el('div', 'gwallet');
  const walletVals = {} as Record<keyof Wallet, HTMLElement>;
  for (const k of ['energy', 'biomass', 'xp', 'enzymes'] as (keyof Wallet)[]) {
    const item = el('span', 'gw-item');
    item.append(el('span', 'gw-icon', ICON[k]));
    const v = el('span', 'gw-val', '0');
    item.append(v);
    gwallet.append(item);
    walletVals[k] = v;
  }

  const closeBtn = el('button', 'gclose', '✕');
  closeBtn.id = 'genomeClose';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close genome');

  header.append(gwallet, closeBtn);

  // ---- body: trait cards grouped by branch ----
  const body = el('div', 'genome-body');
  const cards: Card[] = [];

  // Group traits by branch, preserving TRAITS order within each group.
  const byBranch = new Map<Branch, TraitDef[]>();
  for (const t of TRAITS) {
    const list = byBranch.get(t.branch);
    if (list) list.push(t);
    else byBranch.set(t.branch, [t]);
  }

  for (const branch of BRANCH_ORDER) {
    const group = byBranch.get(branch);
    if (!group || group.length === 0) continue;

    const section = el('section', 'branch');
    section.append(el('h3', 'branch-h', BRANCH_LABEL[branch]));

    for (const trait of group) {
      cards.push(buildCard(section, trait, ctx));
    }
    body.append(section);
  }

  host.append(header, body);

  // ---- render (idempotent): refresh wallet + every card from live state ----
  function render(): void {
    const w = ctx.wallet();
    walletVals.energy.textContent = fmt(w.energy);
    walletVals.biomass.textContent = fmt(w.biomass);
    walletVals.xp.textContent = fmt(w.xp);
    walletVals.enzymes.textContent = fmt(w.enzymes);
    for (const c of cards) c.update();
  }

  // Live: any state change (buying, idle income, feeding) repaints the sheet.
  ctx.onChange(render);

  function open(): void {
    render(); // fill live values before the slide-up
    ctx.startPause();
    document.body.classList.add('genome-open');
    host.scrollTop = 0;
    host.hidden = false; // removing [hidden] triggers the CSS slide-up (translateY 110% -> 0)
    void host.offsetWidth; // force a reflow so the transition runs even on first open
  }

  function close(): void {
    host.hidden = true; // re-adds [hidden] -> slides back down + pointer-events:none
    document.body.classList.remove('genome-open');
    ctx.endPause(); // resume the world
  }

  closeBtn.addEventListener('click', () => {
    ctx.haptic(8);
    close();
  });

  render();

  return { open, close, render };
}

/** Build one trait card, append it to `parent`, and return its refresh closure. */
function buildCard(parent: HTMLElement, trait: TraitDef, ctx: GameCtx): Card {
  const card = el('div', 'gcard');
  if (trait.rare) card.classList.add('rare');
  if (trait.deadEnd) card.classList.add('deadend');

  // --- top row: icon + name + level, plus rare/dead-end tags ---
  const top = el('div', 'gcard-top');
  top.append(el('span', 'gcard-icon', trait.icon));

  const titleWrap = el('div', 'gcard-title');
  titleWrap.append(el('span', 'gcard-name', trait.name));
  const lvlEl = el('span', 'gcard-lvl', '');
  titleWrap.append(lvlEl);
  top.append(titleWrap);

  const tags = el('div', 'gcard-tags');
  if (trait.rare) tags.append(el('span', 'gbadge', `🧪 rare`));
  if (trait.deadEnd) tags.append(el('span', 'gtag', 'dead-end'));
  top.append(tags);

  const blurb = el('p', 'gcard-blurb', trait.blurb);

  const costRow = el('div', 'gcard-cost');

  card.append(top, blurb, costRow);
  parent.append(card);

  // Tap a buyable card -> buy one level, feedback, repaint. No-op when maxed/unaffordable.
  card.addEventListener('click', () => {
    if (!ctx.canBuy(trait.id)) return;
    if (ctx.buyTrait(trait.id)) {
      ctx.sfx('buy');
      ctx.haptic(15);
      // Retrigger the CSS unlock pop.
      card.classList.remove('pop');
      void card.offsetWidth;
      card.classList.add('pop');
      update();
    }
  });

  function update(): void {
    const w = ctx.wallet();
    const L = ctx.lvl(trait.id);
    const maxed = L >= trait.max;
    const buyable = ctx.canBuy(trait.id);

    lvlEl.textContent = `Lv ${L}/${trait.max}`;
    card.classList.toggle('maxed', maxed);
    card.classList.toggle('locked', !maxed && !buyable);

    costRow.replaceChildren();
    if (maxed) {
      costRow.append(el('span', 'gcost-max', 'Fully evolved'));
      return;
    }
    const cost = trait.cost(L);
    for (const k of COST_ORDER) {
      const amt = cost[k];
      if (!amt) continue;
      const short = w[k] < amt;
      costRow.append(el('span', short ? 'chip short' : 'chip', `${ICON[k]} ${fmt(amt)}`));
    }
  }

  return { update };
}
